import React from 'react';
import { Comment } from 'hooks/useCommentsRealtime';

interface AnnotationOverlayProps {
  comments: Comment[];
  currentPictureId?: string;
  imageWidth: number;
  imageHeight: number;
  naturalWidth: number;
  naturalHeight: number;
  onAnnotationClick?: (comment: Comment) => void;
  highlightedCommentId?: string | null;
  isAnnotationMode?: boolean;
}

const AnnotationOverlay: React.FC<AnnotationOverlayProps> = ({
  comments,
  currentPictureId,
  imageWidth,
  imageHeight,
  naturalWidth,
  naturalHeight,
  onAnnotationClick,
  highlightedCommentId,
  isAnnotationMode = false
}) => {
  // Filter comments that have coordinates, belong to current picture, and are NOT resolved
  const annotatedComments = comments.filter(
    (c) => c.coordinates && c.picture_id === currentPictureId && !c.is_resolved
  );

  if (annotatedComments.length === 0) return null;

  // Compute the actual painted image dimensions accounting for object-contain letterboxing.
  // Coordinates were stored relative to the painted image, not the full element.
  let paintedWidth = imageWidth;
  let paintedHeight = imageHeight;
  if (naturalWidth > 0 && naturalHeight > 0) {
    const imgRatio = naturalWidth / naturalHeight;
    const elementRatio = imageWidth / imageHeight;
    if (elementRatio > imgRatio) {
      paintedHeight = imageHeight;
      paintedWidth = paintedHeight * imgRatio;
    } else {
      paintedWidth = imageWidth;
      paintedHeight = paintedWidth / imgRatio;
    }
  }

  return (
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
      <div className="relative" style={{ width: paintedWidth, height: paintedHeight }}>
        {annotatedComments.map((comment, index) => {
          if (!comment.coordinates) return null;

          // Convert relative coordinates (0-1) to absolute pixel positions within the painted image
          const absoluteX = comment.coordinates.x * paintedWidth;
          const absoluteY = comment.coordinates.y * paintedHeight;

          const isHighlighted = highlightedCommentId === comment.comment_id;

          return (
            <button
              key={comment.comment_id}
              onClick={() => onAnnotationClick?.(comment)}
              className={`absolute group ${isAnnotationMode ? 'pointer-events-none' : 'pointer-events-auto'}`}
              style={{
                left: `${absoluteX}px`,
                top: `${absoluteY}px`,
                transform: 'translate(-50%, -50%)'
              }}
            >
              {/* Pin Icon */}
              <div className="relative">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-white shadow-lg transition-all duration-200 border-2 ${
                    isHighlighted
                      ? 'bg-yellow-500 border-yellow-300 scale-125 z-20'
                      : 'bg-blue-600 border-white group-hover:scale-110'
                  }`}
                >
                  <span className="text-xs font-bold">{index + 1}</span>
                </div>

                {/* Animated pulse - more prominent when highlighted */}
                <div
                  className={`absolute inset-0 w-8 h-8 rounded-full animate-ping ${
                    isHighlighted ? 'bg-yellow-400 opacity-100' : 'bg-blue-400 opacity-75'
                  }`}
                ></div>
              </div>

              {/* Tooltip on hover */}
              <div className="absolute top-10 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-xl z-10 min-w-max">
                <div className="font-semibold mb-1">{comment.user_name || comment.user_email}</div>
                <div className="text-gray-300">
                  {comment.message.slice(0, 50)}
                  {comment.message.length > 50 ? '...' : ''}
                </div>
                <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AnnotationOverlay;
